/**
 * Dashboard — KPI tiles + recent-activity feed (refreshed).
 *
 * KPIs are derived client-side from the most-recent payment-intents +
 * inbound-payments pages. A dedicated metrics endpoint is a future
 * surface; the math here is intentionally simple and forgives gaps in
 * data (an empty list yields a zeroed tile, not a crash).
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownToLine,
  Building2,
  CheckCircle2,
  LayoutDashboard,
  Send,
  TrendingUp,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ActivityFeed } from "@/components/dashboard/ActivityFeed";
import { KpiCard } from "@/components/dashboard/KpiCard";
import {
  getInboundPayments,
  getPaymentIntents,
  type PaymentIntentRow,
} from "@/lib/api";
import { formatMinorUnits } from "@/lib/format";
import { PageHeader } from "@/components/shell/PageHeader";
import { useEnvironment } from "@/lib/environment-context";
import { useAuth } from "@/lib/use-auth";

const DAY_MS = 24 * 60 * 60 * 1000;

// All "today" buckets below use the operator's *browser-local* midnight.
// Fine for the current pilot (single Kenyan-timezone audience); revisit
// when operators span time zones — the right answer is server-side
// aggregation per a configured tenant timezone.
function startOfDay(d = new Date()): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

// Picks the most-frequent currency to anchor the headline "Volume today"
// tile. Falls back to KES because the pilot is Kenya-only and a dashboard
// rendered against zero data shouldn't say "USD 0.00". When the platform
// goes multi-currency the right answer is a per-currency stack of tiles
// (or a tenant-default currency in settings) — until then the dominant-
// currency heuristic is the least-confusing single number.
function pickDominantCurrency(rows: ReadonlyArray<{ currency: string }>): string {
  const counts = new Map<string, number>();
  for (const r of rows) counts.set(r.currency, (counts.get(r.currency) ?? 0) + 1);
  let best = "KES";
  let bestN = -1;
  for (const [c, n] of counts) {
    if (n > bestN) {
      bestN = n;
      best = c;
    }
  }
  return best;
}

function sumMinor(rows: ReadonlyArray<{ amount: string; currency: string }>, currency: string): bigint {
  let total = 0n;
  for (const r of rows) {
    if (r.currency !== currency) continue;
    if (!/^-?\d+$/.test(r.amount)) continue;
    total += BigInt(r.amount);
  }
  return total;
}

function bucketByDay(
  rows: ReadonlyArray<{ created_at: string }>,
  days: number,
): number[] {
  const buckets = new Array<number>(days).fill(0);
  const start = startOfDay(new Date(Date.now() - (days - 1) * DAY_MS)).getTime();
  for (const r of rows) {
    const t = new Date(r.created_at).getTime();
    if (t < start) continue;
    const idx = Math.min(days - 1, Math.floor((t - start) / DAY_MS));
    buckets[idx] += 1;
  }
  return buckets;
}

function describeDelta(
  today: number,
  prev: number,
  formatValue: (n: number) => string = String,
): {
  value: string;
  direction: "up" | "down" | "flat";
} {
  if (today === prev) return { value: "—", direction: "flat" };
  // PR #136 review: "+new" reads as a string literal in the delta pill.
  // Show the absolute count as the magnitude when the prior period was
  // zero, with an up arrow on the direction so the pill still leans the
  // right way visually.
  if (prev === 0) return { value: `+${formatValue(today)}`, direction: "up" };
  const pct = Math.round(((today - prev) / prev) * 100);
  return {
    value: `${pct > 0 ? "+" : ""}${pct}%`,
    direction: pct > 0 ? "up" : "down",
  };
}

export function DashboardPage() {
  const { environment } = useEnvironment();
  const { user } = useAuth();

  // Wider page_size for dashboard math — defaults to backend default
  // if the server caps. Two queries in parallel; React Query handles
  // deduping with the per-page caches.
  const intentsQ = useQuery({
    queryKey: ["dashboard", "intents", { environment }] as const,
    queryFn: () => getPaymentIntents({ environment, page_size: 100 }),
  });
  const inboundQ = useQuery({
    queryKey: ["dashboard", "inbound", { environment }] as const,
    queryFn: () => getInboundPayments({ environment, page_size: 100 }),
  });

  const intents = intentsQ.data?.results ?? [];
  const inbound = inboundQ.data?.results ?? [];
  const loading = intentsQ.isLoading || inboundQ.isLoading;

  const kpis = useMemo(() => {
    const now = Date.now();
    const todayStart = startOfDay().getTime();
    const yesterdayStart = todayStart - DAY_MS;
    const last24 = now - DAY_MS;

    const succeeded = intents.filter((i: PaymentIntentRow) => i.state === "succeeded");
    // PR #136 review: "Volume today" is *collected revenue*, not gross
    // movement — adding refunds and payouts to the headline number would
    // mislead an operator scanning the tile. Restrict to ``collect`` and
    // label the tile "Collected today" so the meaning is explicit.
    const collectedSucceeded = succeeded.filter((i) => i.direction === "collect");
    const currency = pickDominantCurrency(
      collectedSucceeded.length ? collectedSucceeded : succeeded.length ? succeeded : intents,
    );

    const collectedToday = collectedSucceeded.filter(
      (i) => new Date(i.created_at).getTime() >= todayStart,
    );
    const collectedYesterday = collectedSucceeded.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t >= yesterdayStart && t < todayStart;
    });

    const volumeToday = sumMinor(collectedToday, currency);
    const volumeYesterday = sumMinor(collectedYesterday, currency);

    const settled = intents.filter(
      (i) => i.state === "succeeded" || i.state === "failed",
    );
    const successRate = settled.length
      ? (intents.filter((i) => i.state === "succeeded").length / settled.length) * 100
      : 0;

    const intentsLast24 = intents.filter(
      (i) => new Date(i.created_at).getTime() >= last24,
    ).length;
    const intentsPrev24 = intents.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t >= last24 - DAY_MS && t < last24;
    }).length;

    const inboundToday = inbound.filter(
      (i) => new Date(i.created_at).getTime() >= todayStart,
    ).length;
    const inboundYesterday = inbound.filter((i) => {
      const t = new Date(i.created_at).getTime();
      return t >= yesterdayStart && t < todayStart;
    }).length;

    return {
      currency,
      volumeToday,
      // Amounts are minor units: "+100" would misread KES 1.00 as 100.
      volumeDelta: describeDelta(Number(volumeToday), Number(volumeYesterday), (n) =>
        formatMinorUnits(String(n), currency),
      ),
      successRate,
      intentsLast24,
      intentsDelta: describeDelta(intentsLast24, intentsPrev24),
      inboundToday,
      inboundDelta: describeDelta(inboundToday, inboundYesterday),
      intentsSpark: bucketByDay(intents, 14),
      inboundSpark: bucketByDay(inbound, 14),
      collectedSpark: bucketByDay(collectedSucceeded, 14),
    };
  }, [intents, inbound]);

  return (
    <div className="space-y-4">
      <PageHeader
        icon={LayoutDashboard}
        title={`Welcome back${user?.display_name ? `, ${user.display_name.split(" ")[0]}` : ""}`}
        description="Here's how your payments are moving today."
        action={
          <Button asChild variant="outline" size="sm">
            <Link to="/payment-intents">
              <Send /> Payment intents
            </Link>
          </Button>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Collected today"
          value={
            loading
              ? "—"
              : formatMinorUnits(kpis.volumeToday.toString(), kpis.currency)
          }
          hint="succeeded collects, vs. yesterday"
          delta={loading ? null : kpis.volumeDelta}
          sparkline={kpis.collectedSpark}
          icon={TrendingUp}
          accent="primary"
          loading={loading}
        />
        <KpiCard
          label="Success rate"
          value={loading ? "—" : `${kpis.successRate.toFixed(1)}%`}
          hint="last page of intents"
          icon={CheckCircle2}
          accent="success"
          loading={loading}
        />
        <KpiCard
          label="Intents (24h)"
          value={loading ? "—" : kpis.intentsLast24.toLocaleString()}
          hint="vs. prior 24h"
          delta={loading ? null : kpis.intentsDelta}
          sparkline={kpis.intentsSpark}
          icon={Send}
          accent="info"
          loading={loading}
        />
        <KpiCard
          label="Inbound today"
          value={loading ? "—" : kpis.inboundToday.toLocaleString()}
          hint="vs. yesterday"
          delta={loading ? null : kpis.inboundDelta}
          sparkline={kpis.inboundSpark}
          icon={ArrowDownToLine}
          accent="warning"
          loading={loading}
        />
      </section>

      <section className="grid grid-cols-1 gap-4 lg:grid-cols-3 [&>*]:min-w-0">
        <div className="lg:col-span-2">
          <ActivityFeed
            intents={intents}
            inbound={inbound}
            loading={loading}
            limit={10}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold">Your access</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Role
              </div>
              <Badge variant="muted" className="capitalize">
                {user?.role}
              </Badge>
            </div>
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Tenants
              </div>
              <div className="font-medium">
                {user?.scoped_to_all_tenants
                  ? "All tenants (super-admin)"
                  : `${user?.tenants.length ?? 0} tenant${
                      (user?.tenants.length ?? 0) === 1 ? "" : "s"
                    }`}
              </div>
            </div>
            <div className="space-y-1">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">
                Session expires
              </div>
              <div
                className="font-mono text-xs"
                title={user?.session.expires_at}
                data-testid="session-expires"
              >
                {user
                  ? new Date(user.session.expires_at).toLocaleString()
                  : "—"}
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/tenants" className="justify-start">
                  <Building2 /> Manage tenants
                </Link>
              </Button>
              <Button asChild variant="outline" size="sm">
                <Link to="/payment-intents" className="justify-start">
                  <Send /> Browse payment intents
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

export default DashboardPage;
